import React from "react";

import {
  ActionButton,
  confirm,
  dataAttribute,
  DataAttributes,
  NativeList,
  Stack,
  Text,
  TooltipHost
} from "@bps/fluent-ui";
import { ProviderOnlineStatus } from "@libs/gateways/bhb/bhbGateway.dtos.ts";
import { SecurityRoleCode } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { OrgUnit } from "@stores/core/models/OrgUnit.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Navigate } from "@ui-components/navigation/Navigate.tsx";

import { MAX_ACTIVE_LOCATIONS } from "./PracticeLocationAddButton.tsx";
import { OrgUnitScreenData } from "./utils.ts";

interface LocationStatusButtonsProps extends OrgUnitScreenData {
  onReactivate: () => void;
}

enum Titles {
  deactivateLocation = "Deactivate location",
  activateLocation = "Activate location"
}

export const LocationStatusToggleButton: React.FC<
  LocationStatusButtonsProps
> = ({
  orgUnit,
  hasAppointments,
  hasMeetings,
  hasUnavailables,
  hasFutureWaitingListEntries,
  onReactivate
}) => {
  const { core, notification, booking, bhb } = useStores();

  if (!core.hasSecurityRoles([SecurityRoleCode.PracInfoAdmin])) return null;

  const removeUserAvailabilityCacheByLocationId = (locationId: string) => {
    // Deactivating a location will result in working hours for each provider at that location being changed
    // Since there is no SignalR events for those changes, we just wipe the FE cache so the app will re-get
    //  those working hours.  This is not an ideal solution because other users and other tabs will be unaffected
    //  but those situation will be unlikely to matter so we decided not to spend time developing a better solution: PBI 43128
    booking.userAvailabilityMap.forEach((userAvailabilityModel, key) => {
      if (userAvailabilityModel.availableOrgUnitIds.includes(locationId)) {
        booking.userAvailabilityMap.delete(key);
      }
    });
  };

  const handleConfirmationButtonClick = async (location: OrgUnit) => {
    await core.updateOrgUnit({
      id: location.id,
      isInactive: !location.isInactive
    });

    notification.success(
      `Location ${location.name} is now ${
        location.isInactive ? "deactivated" : "activated"
      }`
    );

    if (location.isInactive) {
      removeUserAvailabilityCacheByLocationId(orgUnit.id);
    } else {
      onReactivate();
    }
  };

  const isInactiveLocation = !!orgUnit?.isInactive;

  // This should probably be replaced with something standard
  const buttonStyles = {
    root: {
      padding: "6px 16px"
    }
  };

  const numberOfActiveLocation = core.userOrgUnits.reduce(
    (sum, locationId) => (core.hasInactiveFlag(locationId) ? sum : sum + 1),
    0
  );

  const isLocationNameInUse = core.activeLocations
    .map(x => x.name)
    .includes(orgUnit.name);

  const reachedMaxActiveLocations =
    numberOfActiveLocation >= MAX_ACTIVE_LOCATIONS;

  const getTooltipText = (): string | JSX.Element | undefined => {
    if (!isInactiveLocation) {
      if (isDefaultLocation) {
        return "The default location cannot be deactivated.";
      }
      if (isOnlyLocation) {
        return "You must add another location before you can deactivate this location.";
      }

      if (
        hasAppointments ||
        hasMeetings ||
        hasUnavailables ||
        hasFutureWaitingListEntries
      ) {
        return (
          <Stack tokens={{ childrenGap: 16 }}>
            <Text>
              The following items are still recorded for this location. Remove
              them before deactivating
            </Text>
            <NativeList>
              {hasAppointments && <li>Booked appointments</li>}
              {hasFutureWaitingListEntries && <li>Waiting list patients</li>}
              {hasMeetings && <li>Meetings</li>}
              {hasUnavailables && <li>Unavailable sessions</li>}
            </NativeList>
            <Navigate to={routes.calendarEvents.basePath.pattern}>
              Go to appointment book
            </Navigate>
          </Stack>
        );
      }
    } else if (reachedMaxActiveLocations) {
      return `The maximum of ${MAX_ACTIVE_LOCATIONS} active locations has been reached. You must deactivate a location before reactivating this one`;
    } else if (isLocationNameInUse) {
      return "Location name already used by an active location. Please update active location name before reactivating";
    }

    return undefined;
  };

  const onClick = async () => {
    const isConfirmed = await confirm({
      cancelButtonProps: {
        text: "Cancel"
      },
      confirmButtonProps: {
        text: isInactiveLocation
          ? Titles.activateLocation
          : Titles.deactivateLocation
      },
      dialogContentProps: {
        title: isInactiveLocation
          ? Titles.activateLocation
          : Titles.deactivateLocation,
        subText: isInactiveLocation
          ? "Are you sure you want to activate this location?"
          : "Any current working hours for this location will be deleted. Are you sure you want to deactivate this location?"
      }
    });

    if (!isInactiveLocation) {
      const bhbProviders = await bhb
        .getProvidersForLocation(orgUnit.id)
        .catch(() => []);
      await Promise.all(
        bhbProviders.map(provider =>
          provider.providerOnlineStatus === ProviderOnlineStatus.NO
            ? undefined
            : bhb.updateProvider({
                ...provider.dto,
                providerOnlineStatus: ProviderOnlineStatus.NO
              })
        )
      );
    }

    if (isConfirmed) {
      await handleConfirmationButtonClick(orgUnit);
    }
  };

  const isDefaultLocation = !!orgUnit?.isDefaultLocation;
  const isOnlyLocation = core.userOrgUnits.length === 1;

  const isDisabled = isInactiveLocation
    ? reachedMaxActiveLocations || isLocationNameInUse
    : isDefaultLocation ||
      isOnlyLocation ||
      hasAppointments ||
      hasFutureWaitingListEntries;
  return (
    <TooltipHost content={getTooltipText()}>
      <ActionButton
        onClick={onClick}
        styles={buttonStyles}
        disabled={isDisabled}
        {...dataAttribute(
          DataAttributes.Element,
          isInactiveLocation
            ? "location-is-active-button"
            : "location-is-inactive-button"
        )}
      >
        {isInactiveLocation ? "Activate" : "Deactivate"}
      </ActionButton>
    </TooltipHost>
  );
};
